/**
 * Input log codec: every round submits its seed, header and input log so the
 * server can replay it (PRD §8 anti-cheat). Entries are [dt, op, arg] varints,
 * about 2 bytes per tap.
 */
import { Op, type Action } from './types.js';

/** Economy numbers a round was played with (economy.json `round`); absent = core defaults. */
export interface RoundEconomy {
  coinsOrderPop: number;
  coinsClutterPop: number;
  coinsSweep: number;
  rescueTimeMs: number;
}

export interface RoundHeader {
  /** the client engine's RULES_VERSION: at least the level's, at most the verifier's (rulesVersionAccepted) */
  rulesVersion: number;
  levelId: string;
  levelHash: string;
  seed: number;
  assist: number;
  rescueThreshold: number;
  maxRescues: number;
  /** Tunable round economy (A/B-testable). The server checks it against the player's assigned economy. */
  cfg?: RoundEconomy;
}

function pushVarint(out: number[], v: number): void {
  let x = v >>> 0;
  while (x >= 0x80) {
    out.push((x & 0x7f) | 0x80);
    x >>>= 7;
  }
  out.push(x);
}

function readVarint(b: Uint8Array, pos: { p: number }): number {
  let x = 0;
  let shift = 0;
  for (;;) {
    if (pos.p >= b.length) throw new Error('log truncated');
    const c = b[pos.p++]!;
    x += (c & 0x7f) * 2 ** shift;
    if (c < 0x80) break;
    shift += 7;
    if (shift > 35) throw new Error('varint too long');
  }
  return x;
}

export function encodeLog(actions: readonly Action[]): Uint8Array {
  const out: number[] = [];
  let prev = 0;
  for (const a of actions) {
    const dt = a.t - prev;
    if (!Number.isInteger(dt) || dt < 0 || a.t > 0xffffffff) throw new Error(`log time must be monotonic uint32 (t=${a.t}, prev=${prev})`);
    if (a.op === Op.Tap && (!Number.isInteger(a.arg) || a.arg! < 0 || a.arg! > 0xffff)) throw new Error(`bad tap arg ${a.arg}`);
    pushVarint(out, dt);
    prev = a.t;
    out.push(a.op);
    if (a.op === Op.Tap) pushVarint(out, a.arg ?? 0);
  }
  return Uint8Array.from(out);
}

export function decodeLog(bytes: Uint8Array): Action[] {
  const out: Action[] = [];
  const pos = { p: 0 };
  let t = 0;
  while (pos.p < bytes.length) {
    t += readVarint(bytes, pos);
    if (pos.p >= bytes.length) throw new Error('log truncated');
    const op = bytes[pos.p++]! as Op;
    if (op > Op.Tick) throw new Error('bad op');
    if (op === Op.Tap) out.push({ op, t, arg: readVarint(bytes, pos) });
    else out.push({ op, t });
  }
  return out;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function toBase64Url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    s += B64[a >> 2]! + B64[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    if (b !== undefined) s += B64[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    if (c !== undefined) s += B64[c & 63]!;
  }
  return s;
}

export function fromBase64Url(s: string): Uint8Array {
  const out: number[] = [];
  let buf = 0;
  let bits = 0;
  for (const ch of s) {
    const v = B64.indexOf(ch);
    if (v < 0) throw new Error('bad base64url');
    buf = (buf << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buf >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}
